import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDiagnosticReceipt, useVisitTimelineByVisit } from '../../services/httpServices/queries';
import { Loader2, AlertCircle, ArrowLeft, History } from 'lucide-react';

/**
 * Standalone Receipt History screen. The timeline used to live at the bottom of
 * the receipt editor; it now has its own page reached from the editor's toolbar
 * so the edit form stays focused on the money fields.
 */
export function DiagnosticReceiptHistory() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, error } = useDiagnosticReceipt(id);
  const receipt = data?.data;
  const { data: timelineData, isLoading: timelineLoading } = useVisitTimelineByVisit(receipt?.visitId);

  const fromDiagnostics = location.pathname.startsWith('/diagnostics');
  const backPath = fromDiagnostics
    ? `/diagnostics/receipts/${id}`
    : `/opd/diagnostic-receipts/${id}`;

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(backPath)}
          className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors cursor-pointer"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-text">Receipt History</h1>
          <p className="text-sm text-text-muted">
            {receipt ? (
              <>
                Timeline for receipt{' '}
                <span className="font-mono font-medium text-text">{receipt.receiptNumber}</span>
              </>
            ) : (
              'Timeline of events recorded for this receipt'
            )}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : error || !receipt ? (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load diagnostic receipt</span>
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border p-6 space-y-4">
          <div className="flex items-center gap-2">
            <History size={18} className="text-primary" />
            <h2 className="text-sm font-semibold text-text uppercase tracking-wide">Receipt History</h2>
          </div>

          {!receipt.visitId ? (
            <p className="text-sm text-text-muted">
              This receipt is not linked to a visit, so no history is available.
            </p>
          ) : timelineLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-3">
              {(timelineData?.data || []).length === 0 ? (
                <p className="text-sm text-text-muted">No history events recorded yet.</p>
              ) : (
                (timelineData?.data || []).map((event: any) => (
                  <div
                    key={event.id}
                    className="flex items-start gap-3 p-3 bg-background rounded-lg border border-border"
                  >
                    <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text">
                        {event.eventType.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-text-muted mt-0.5">{event.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-text-muted bg-surface px-1.5 py-0.5 rounded">
                          {event.sourceModule}
                        </span>
                        <span className="text-[10px] text-text-muted">
                          {new Date(event.eventTimestamp).toLocaleString()}
                        </span>
                        {event.performedBy?.name && (
                          <span className="text-[10px] text-text-muted">
                            by {event.performedBy.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
